import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { clientsAPI } from '../../services/api';

const BASE = '/api';
function authFetch(url, opts={}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers||{}), ...(token?{Authorization:`Bearer ${token}`}:{}) } });
}

const STATUT_COLORS = {
  ouverture_pendante: { bg:'#FEF3C7', color:'#92400E', dot:'#F59E0B' },
  ouverte:            { bg:'#FEE2E2', color:'#991B1B', dot:'#EF4444' },
  cloturee:           { bg:'#F1F5F9', color:'#475569', dot:'#94A3B8' },
};

function fmtDate(d, lang) { return d ? new Date(d).toLocaleDateString(lang==='en'?'en-GB':'fr-FR') : '—'; }

export default function ListeSinistres() {
  const { t, lang }  = useLanguage();
  const { isManager } = useAuth();
  const navigate = useNavigate();

  // Labels statuts (dépendent de la langue)
  const STATUT_LABELS = {
    ouverture_pendante: t('sinistres.statut_ouverture_pendante'),
    ouverte:            t('sinistres.statut_ouverte'),
    cloturee:           t('sinistres.statut_cloturee'),
  };

  const [sinistres, setSinistres]     = useState([]);
  const [clients, setClients]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [showModal, setShowModal]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingSinistre, setPendingSinistre] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  // Form state
  const [clientId, setClientId]   = useState('');
  const [dateOuv, setDateOuv]     = useState(new Date().toISOString().slice(0,10));
  const [dateFin, setDateFin]     = useState('');
  const [notes, setNotes]         = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await authFetch(`${BASE}/sinistres${filtreStatut!=='tous'?`?statut=${filtreStatut}`:''}`);
      const data = await res.json();
      setSinistres(Array.isArray(data) ? data : []);
    } catch { setSinistres([]); }
    finally { setLoading(false); }
  }, [filtreStatut]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    clientsAPI.list().then(setClients).catch(()=>{});
  }, []);

  async function ouvrirProcedure() {
    if (!clientId || !dateOuv) { setError(t('sinistres.err_required')); return; }
    setSaving(true); setError('');
    try {
      const res  = await authFetch(`${BASE}/sinistres`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ client_id: clientId, date_ouverture: dateOuv,
          date_fin_indemnisations: dateFin||undefined, notes: notes||undefined }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      // Afficher la confirmation admin avant de naviguer
      setPendingSinistre(data);
      setShowModal(false);
      setShowConfirm(true);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function confirmerOuverture() {
    if (!pendingSinistre) return;
    setSaving(true);
    try {
      const res  = await authFetch(`${BASE}/sinistres/${pendingSinistre.id}/confirmer`, { method:'POST' });
      const data = await res.json();
      if (data.error) { setError(data.error); setShowConfirm(false); return; }
      setShowConfirm(false);
      setPendingSinistre(null);
      navigate(`/sinistres/${data.id}`);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function annulerProcedure() {
    // Supprimer la procédure en attente
    if (pendingSinistre) {
      await authFetch(`${BASE}/sinistres/${pendingSinistre.id}`, { method:'DELETE' }).catch(()=>{});
    }
    setShowConfirm(false); setPendingSinistre(null);
  }

  const clientsPourSinistre = clients.filter(c => c.statut === 'actif' || c.statut === 'prospect');
  const selectedClient = clients.find(c => c.id === clientId);

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, margin:0 }}>{t('sinistres.title')}</h1>
          <p style={{ fontSize:13, color:'#64748B', margin:'4px 0 0' }}>
            {t('sinistres.subtitle')}
          </p>
        </div>
        <button onClick={() => { setShowModal(true); setError(''); setClientId(''); setDateOuv(new Date().toISOString().slice(0,10)); setDateFin(''); setNotes(''); }}
          style={{ background:'#E8671A', color:'#fff', border:'none', borderRadius:8,
            padding:'10px 18px', fontSize:14, fontWeight:700, cursor:'pointer',
            display:'flex', alignItems:'center', gap:8 }}>
          ⚖ {t('sinistres.open_procedure')}
        </button>
      </div>

      {/* Filtres */}
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {['tous','ouverture_pendante','ouverte','cloturee'].map(s => (
          <button key={s} onClick={() => setFiltreStatut(s)}
            style={{ padding:'5px 14px', borderRadius:99, fontSize:12, cursor:'pointer',
              fontWeight: filtreStatut===s ? 700 : 400,
              background: filtreStatut===s ? '#1A1A1A' : '#F1F5F9',
              color: filtreStatut===s ? '#fff' : '#64748B', border:'none' }}>
            {s==='tous' ? t('sinistres.filter_all') : STATUT_LABELS[s] || s}
          </button>
        ))}
      </div>

      {/* Liste */}
      <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E2E8F0', overflow:'hidden' }}>
        {/* En-tête */}
        <div style={{ display:'grid', gridTemplateColumns:'1.2fr 2fr 1.4fr 1fr 1fr 0.7fr',
          padding:'10px 16px', background:'#F8FAFC', fontSize:11, fontWeight:700,
          color:'#64748B', textTransform:'uppercase', letterSpacing:'.05em' }}>
          <div>{t('sinistres.col_numero')}</div>
          <div>{t('sinistres.col_agence')}</div>
          <div>{t('sinistres.col_statut')}</div>
          <div>{t('sinistres.col_date_ouv')}</div>
          <div>{t('sinistres.col_date_fin')}</div>
          <div>{t('sinistres.col_actions')}</div>
        </div>

        {loading && (
          <div style={{ padding:24, textAlign:'center', color:'#94A3B8' }}>{t('sinistres.loading')}</div>
        )}
        {!loading && sinistres.length === 0 && (
          <div style={{ padding:32, textAlign:'center', color:'#94A3B8', fontSize:13 }}>
            {t('sinistres.no_procedures')}
          </div>
        )}
        {sinistres.map((s, i) => {
          const st = STATUT_COLORS[s.statut] || STATUT_COLORS.cloturee;
              const stLabel = STATUT_LABELS[s.statut] || s.statut;
          return (
            <div key={s.id}
              style={{ display:'grid', gridTemplateColumns:'1.2fr 2fr 1.4fr 1fr 1fr 0.7fr',
                padding:'13px 16px', borderTop: i>0?'1px solid #F1F5F9':'none',
                alignItems:'center',
                background: s.statut==='ouverte' ? '#FFFBF5' : '#fff' }}>
              <div style={{ fontWeight:700, fontSize:13, fontFamily:'monospace', color:'#1A1A1A' }}>
                {s.numero_procedure}
              </div>
              <div>
                <div style={{ fontWeight:600, fontSize:14 }}>{s.client_nom}</div>
                {s.client_locked && (
                  <span style={{ fontSize:10, color:'#64748B' }}>🔒 {t('sinistres.agence_locked')}</span>
                )}
              </div>
              <div>
                <span style={{ display:'inline-flex', alignItems:'center', gap:5,
                  background:st.bg, color:st.color, borderRadius:99, padding:'3px 10px', fontSize:12, fontWeight:600 }}>
                  <span style={{ width:7,height:7,borderRadius:'50%',background:st.dot,flexShrink:0 }}/>
                  {stLabel}
                </span>
              </div>
              <div style={{ fontSize:13, color:'#475569' }}>{fmtDate(s.date_ouverture, lang)}</div>
              <div style={{ fontSize:13, color:'#475569' }}>{fmtDate(s.date_fin_indemnisations, lang)}</div>
              <div>
                <button onClick={() => navigate(`/sinistres/${s.id}`)}
                  style={{ fontSize:12, padding:'5px 12px', borderRadius:6,
                    background:'#F1F5F9', color:'#1A1A1A', border:'1px solid #E2E8F0',
                    cursor:'pointer', fontWeight:600 }}>
                  {t('sinistres.open_btn')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Modal : Ouvrir une procédure ──────────────────────── */}
      {showModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'#fff',borderRadius:12,padding:28,width:520,maxWidth:'92vw' }}>
            <div style={{ fontSize:18,fontWeight:800,marginBottom:4 }}>⚖ {t('sinistres.open_procedure')}</div>
            <div style={{ fontSize:13,color:'#64748B',marginBottom:20 }}>
              {t('sinistres.modal_subtitle')}
            </div>

            {error && (
              <div style={{ background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:7,
                padding:'8px 12px',marginBottom:14,fontSize:13,color:'#991B1B' }}>⚠ {error}</div>
            )}

            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5 }}>
                {t('sinistres.col_agence')} *
              </label>
              <select value={clientId} onChange={e=>setClientId(e.target.value)}
                style={{ border:'1.5px solid #E2E8F0',borderRadius:7,padding:'9px 12px',
                  width:'100%',fontSize:13,fontFamily:'inherit',boxSizing:'border-box' }}>
                <option value="">{t('sinistres.select_agence')}</option>
                {clientsPourSinistre.map(c => (
                  <option key={c.id} value={c.id}>{c.nom} ({c.marche_pays})</option>
                ))}
              </select>
            </div>

            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14 }}>
              <div>
                <label style={{ fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5 }}>
                  {t('sinistres.date_ouverture')}
                </label>
                <input type="date" value={dateOuv} onChange={e=>setDateOuv(e.target.value)}
                  style={{ border:'1.5px solid #E2E8F0',borderRadius:7,padding:'9px 12px',
                    width:'100%',fontSize:13,boxSizing:'border-box' }}/>
              </div>
              <div>
                <label style={{ fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5 }}>
                  {t('sinistres.date_fin')}
                </label>
                <input type="date" value={dateFin} onChange={e=>setDateFin(e.target.value)}
                  min={dateOuv}
                  style={{ border:'1.5px solid #E2E8F0',borderRadius:7,padding:'9px 12px',
                    width:'100%',fontSize:13,boxSizing:'border-box' }}/>
              </div>
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5 }}>
                {t('sinistres.notes')}
              </label>
              <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
                placeholder={t('sinistres.notes_placeholder')}
                style={{ border:'1.5px solid #E2E8F0',borderRadius:7,padding:'9px 12px',
                  width:'100%',fontSize:13,boxSizing:'border-box',resize:'vertical',fontFamily:'inherit' }}/>
            </div>

            <div style={{ background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:7,
              padding:'10px 14px',fontSize:12,color:'#92400E',marginBottom:20,display:'flex',gap:8 }}>
              <span style={{ fontSize:16 }}>⚠</span>
              <span>{t('sinistres.warning_manager')}</span>
            </div>

            <div style={{ display:'flex',justifyContent:'flex-end',gap:8 }}>
              <button onClick={() => setShowModal(false)}
                style={{ fontSize:14,padding:'8px 18px',borderRadius:7,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff' }}>
                {t('common.cancel')}
              </button>
              <button onClick={ouvrirProcedure} disabled={!clientId||!dateOuv||saving}
                style={{ fontSize:14,padding:'8px 18px',borderRadius:7,background:'#E8671A',
                  color:'#fff',border:'none',cursor:'pointer',fontWeight:700,
                  opacity:(!clientId||!dateOuv||saving)?.5:1 }}>
                {saving ? t('sinistres.creation') : t('sinistres.valider')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Dialog : Confirmation Manager ─────────────────────── */}
      {showConfirm && pendingSinistre && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.55)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:1001 }}>
          <div style={{ background:'#fff',borderRadius:12,padding:28,width:480,maxWidth:'92vw',
            border:'3px solid #DC2626' }}>
            <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:12 }}>
              <span style={{ fontSize:28 }}>⚠</span>
              <div>
                <div style={{ fontSize:17,fontWeight:800,color:'#DC2626' }}>{t('sinistres.confirm_title')}</div>
                <div style={{ fontSize:12,color:'#64748B' }}>{t('sinistres.confirm_subtitle')}</div>
              </div>
            </div>
            <div style={{ background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,
              padding:'12px 16px',marginBottom:20,fontSize:13 }}>
              <p style={{ margin:'0 0 8px',color:'#374151' }}>
                {t('sinistres.confirm_about')}
              </p>
              <p style={{ margin:'0 0 8px',fontSize:16,fontWeight:800,color:'#1A1A1A' }}>
                🏢 {pendingSinistre.client_nom}
              </p>
              <p style={{ margin:'0 0 4px',fontSize:12,color:'#64748B' }}>
                N° {pendingSinistre.numero_procedure} · Ouverture : {fmtDate(pendingSinistre.date_ouverture, lang)}
              </p>
              <p style={{ margin:'8px 0 0',fontSize:12,color:'#DC2626',fontWeight:600 }}>
                {t('sinistres.confirm_warning')}
              </p>
            </div>

            {!isManager && (
              <div style={{ background:'#FEE2E2',borderRadius:7,padding:'8px 12px',
                fontSize:12,color:'#991B1B',marginBottom:14 }}>
                🔒 {t('sinistres.confirm_no_manager')}
              </div>
            )}

            {error && (
              <div style={{ background:'#FEE2E2',borderRadius:7,padding:'8px 12px',
                fontSize:12,color:'#991B1B',marginBottom:14 }}>⚠ {error}</div>
            )}

            <div style={{ display:'flex',justifyContent:'flex-end',gap:8 }}>
              <button onClick={annulerProcedure}
                style={{ fontSize:14,padding:'8px 18px',borderRadius:7,
                  border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff' }}>
                {t('sinistres.annuler_procedure')}
              </button>
              <button onClick={confirmerOuverture} disabled={!isManager||saving}
                style={{ fontSize:14,padding:'8px 18px',borderRadius:7,background:'#DC2626',
                  color:'#fff',border:'none',cursor:'pointer',fontWeight:700,
                  opacity:(!isManager||saving)?.5:1 }}>
                {saving ? t('sinistres.confirmation_loading') : t('sinistres.confirmer_ouverture')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
